import { ArrowDown, ArrowUp, ArrowUpDown, Cake, Check, MapPin, MessageCircle, Phone, School } from "lucide-react";

const getRelativeTime = (dateStr) => {
  if (!dateStr || dateStr === "-") return "-";
  const date = new Date(dateStr);
  const yy = String(date.getFullYear()).slice(2);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

export default function StudentListContentSection({
  viewMode,
  sortedStudents,
  selectedIds,
  demoAction,
  visitLogs,
  GlassCard,
  handleSelectRow,
  setViewingStudent,
  userProfile,
  icons,
  faithStateStyles,
  openAddModal,
  sortKey,
  sortOrder,
  onSort,
}) {
  const SortIcon = ({ columnKey }) => {
    if (sortKey !== columnKey) return <ArrowUpDown size={12} className="opacity-30 group-hover/header:opacity-100 transition-opacity ml-1" />;
    return sortOrder === "asc" ? <ArrowUp size={12} className="ml-1 text-[#354F38]" /> : <ArrowDown size={12} className="ml-1 text-[#354F38]" />;
  };

  return viewMode === "card" ? (
    // ... (card view remains unchanged)
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
      {sortedStudents.map((s, idx) => {
        const isSelected = selectedIds.includes(s.id);
        const isNewBeliever = s.faithState === "초신자";
        const lastAttendedDays = s.lastAttendance
          ? Math.ceil(
            Math.abs(new Date() - new Date(s.lastAttendance)) / (1000 * 60 * 60 * 24)
          )
          : 999;
        const registeredDays = s.registeredAt || s.createdAt
          ? Math.ceil(
            Math.abs(new Date() - new Date(s.registeredAt || s.createdAt)) /
            (1000 * 60 * 60 * 24)
          )
          : 0;
        const isLongAbsence = lastAttendedDays > 60 && registeredDays > 60;
        const hasPrayerRequest = visitLogs.some(
          (l) => l.studentId === s.id && l.type === "기도"
        );

        return (
          <GlassCard
            id={idx === 0 ? "tour-student-card" : null}
            key={s.id}
            onClick={() => handleSelectRow(s.id)}
            className={`
              relative rounded-[2rem] overflow-hidden transition-all duration-500 cursor-pointer group border
              ${isSelected
                ? "bg-white ring-4 ring-[#354F38]/20 border-[#354F38] z-10 scale-[1.02]"
                : idx === 0 && demoAction === "spotlight"
                  ? "bg-white ring-8 ring-yellow-400/50 border-yellow-400 shadow-[0_0_50px_rgba(255,215,0,0.6)] z-50 scale-110 !opacity-100 !grayscale-0 transform translate-y-[-10px]"
                  : "bg-white hover:border-gray-300 hover:shadow-xl hover:-translate-y-1 border-gray-100 shadow-sm"
              }
              ${isLongAbsence && !isSelected && !(idx === 0 && demoAction === "spotlight")
                ? "grayscale opacity-70 hover:grayscale-0 hover:opacity-100"
                : ""
              }
            `}
          >
            <div
              className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 z-20 ${isSelected
                ? "bg-[#354F38] border-[#354F38] scale-110"
                : "border-gray-200 opacity-0 group-hover:opacity-100 bg-white"
                }`}
            >
              {isSelected && <Check size={14} className="text-white" />}
            </div>

            {isLongAbsence && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-md border border-red-100 text-red-500 text-[10px] font-black px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-30 pointer-events-none whitespace-nowrap shadow-[0_4px_20px_rgba(239,68,68,0.15)] flex items-center gap-1 transform translate-y-1 group-hover:translate-y-0">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                장기 결석 (2달)
              </div>
            )}

            {hasPrayerRequest && (
              <div className="absolute top-4 left-4 z-20" title="기도제목 있음">
                <div className="w-8 h-8 rounded-full bg-purple-50 border border-purple-100 flex items-center justify-center shadow-sm animate-bounce-slow">
                  <img src={icons.prayer} width={18} alt="prayer" />
                </div>
              </div>
            )}

            <div className="p-4 md:p-6 flex flex-col items-center text-center h-full">
              <div
                className="relative mb-4 group/avatar"
                onClick={(e) => {
                  e.stopPropagation();
                  setViewingStudent(s);
                }}
              >
                <div
                  className={`w-20 h-20 md:w-28 md:h-28 rounded-[2rem] overflow-hidden shadow-lg border-[5px] border-white bg-gray-50 transition-transform duration-500 group-hover/avatar:scale-105 ${isNewBeliever ? "ring-4 ring-yellow-400/50" : ""
                    }`}
                >
                  {s.photoUrl ? (
                    <img src={s.photoUrl} alt={s.name} className="w-full h-full object-cover" />
                  ) : (
                    <img
                      src={s.gender === "남" ? "/images/boy_3d_light.png" : "/images/girl_3d_light.png"}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                {isNewBeliever && (
                  <div className="absolute -bottom-1 -right-1 bg-yellow-400 text-white text-[10px] font-black px-2 py-1 rounded-full shadow-sm border-[3px] border-white z-10 animate-pulse">
                    NEW
                  </div>
                )}
              </div>

              <div className="mb-6 w-full">
                <h3 className="text-2xl font-black text-gray-900 mb-1 tracking-tight">{s.name}</h3>
                <p className="text-gray-400 font-bold text-sm">
                  {s.grade} {s.class}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 w-full mb-6">
                <div
                  className={`py-2 rounded-xl border flex flex-col items-center justify-center transition-colors ${s.lastAttendance
                    ? lastAttendedDays < 14
                      ? "bg-green-50 border-green-100 text-green-700"
                      : "bg-orange-50 border-orange-100 text-orange-600"
                    : "bg-gray-50 border-gray-100 text-gray-400"
                    }`}
                >
                  <span className="text-[10px] font-bold opacity-70 mb-0.5">출석</span>
                  <span className="text-xs font-black">{getRelativeTime(s.lastAttendance)}</span>
                </div>
                <div
                  className={`py-2 rounded-xl border flex flex-col items-center justify-center transition-colors ${s.lastVisit
                    ? "bg-orange-50 border-orange-100 text-orange-600"
                    : "bg-gray-50 border-gray-100 text-gray-400"
                    }`}
                >
                  <span className="text-[10px] font-bold opacity-70 mb-0.5">심방</span>
                  <span className="text-xs font-black">{getRelativeTime(s.lastVisit)}</span>
                </div>
              </div>

              <div className="flex gap-2 w-full mt-auto opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                <a
                  href={`tel:${s.phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className={`flex-1 py-3 rounded-2xl text-white font-bold text-sm shadow-lg shadow-[#354F38]/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 ${userProfile?.departmentColors?.[s.grade]
                    ? userProfile.departmentColors[s.grade]
                    : "bg-[#354F38] hover:bg-[#2C3E30]"
                    }`}
                >
                  <Phone size={16} fill="currentColor" />
                </a>
                <a
                  href={`sms:${s.phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 py-3 rounded-2xl bg-[#FFECA1] text-[#4A3B10] font-bold text-sm shadow-lg shadow-yellow-400/20 hover:bg-[#FFD54F] hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  <MessageCircle size={16} />
                </a>
              </div>
            </div>
          </GlassCard>
        );
      })}

      {sortedStudents.length === 0 && (
        <div className="col-span-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="relative group w-full select-none">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 text-center w-full px-4">
                <span className="bg-[#354F38] text-white border-2 border-white text-sm font-black px-5 py-2.5 rounded-full shadow-2xl inline-block whitespace-nowrap mb-3">
                  ✨ 첫 학생을 등록해보세요!
                </span>
                <br />
                <button
                  onClick={openAddModal}
                  className="bg-white text-[#354F38] border border-gray-100 font-bold px-6 py-2 rounded-full shadow-xl hover:scale-105 transition-transform"
                >
                  지금 등록하기
                </button>
              </div>
              <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 rounded-[2rem]"></div>
              <GlassCard className="relative rounded-[2rem] overflow-hidden border border-gray-200 bg-white opacity-50 grayscale scale-[0.98]">
                <div className="p-6 flex flex-col items-center text-center h-full">
                  <div className="w-28 h-28 rounded-[2rem] bg-gray-100 mb-4 shadow-sm"></div>
                  <div className="w-24 h-6 bg-gray-100 rounded-full mb-2"></div>
                  <div className="w-16 h-4 bg-gray-100 rounded-full mb-6"></div>
                  <div className="grid grid-cols-2 gap-2 w-full mb-6">
                    <div className="h-12 bg-gray-50 rounded-xl"></div>
                    <div className="h-12 bg-gray-50 rounded-xl"></div>
                  </div>
                  <div className="flex gap-2 w-full mt-auto">
                    <div className="flex-1 h-10 bg-gray-100 rounded-2xl"></div>
                    <div className="flex-1 h-10 bg-gray-100 rounded-2xl"></div>
                  </div>
                </div>
              </GlassCard>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : (
    <div className="bg-white/60 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-sm overflow-hidden flex flex-col h-full">
      <div className="p-6 border-b border-gray-100/50 flex justify-between items-center bg-white/40 flex-shrink-0">
        <h3 className="font-black text-gray-800 ml-2 text-lg">
          전체 목록{" "}
          <span className="text-gray-400 font-medium text-sm ml-1">({sortedStudents.length}명)</span>
        </h3>
      </div>

      <div className="flex-1 overflow-auto custom-scrollbar p-4">
        <div className="w-full min-w-[900px]">
          <div className="grid grid-cols-[50px_1.5fr_1fr_1.5fr_0.8fr_0.8fr_0.8fr_1fr] gap-2 px-4 py-3 bg-gray-50/80 backdrop-blur-md rounded-2xl mb-3 sticky top-0 z-20 border border-gray-100 text-gray-500 text-xs font-bold uppercase tracking-wider">
            <div className="text-center">#</div>
            <div
              className="flex items-center cursor-pointer hover:text-[#354F38] transition-colors group/header"
              onClick={() => onSort("name")}
            >
              학생 정보 <SortIcon columnKey="name" />
            </div>
            <div
              className="flex items-center cursor-pointer hover:text-[#354F38] transition-colors group/header"
              onClick={() => onSort("birth")}
            >
              인적 사항 <SortIcon columnKey="birth" />
            </div>
            <div
              className="flex items-center cursor-pointer hover:text-[#354F38] transition-colors group/header"
              onClick={() => onSort("phone")}
            >
              연락처/주소 <SortIcon columnKey="phone" />
            </div>
            <div
              className="flex items-center cursor-pointer hover:text-[#354F38] transition-colors group/header"
              onClick={() => onSort("faithState")}
            >
              신앙상태 <SortIcon columnKey="faithState" />
            </div>
            <div
              className="flex items-center cursor-pointer hover:text-[#354F38] transition-colors group/header"
              onClick={() => onSort("lastAttendance")}
            >
              최근 출석 <SortIcon columnKey="lastAttendance" />
            </div>
            <div
              className="flex items-center cursor-pointer hover:text-[#354F38] transition-colors group/header"
              onClick={() => onSort("lastVisit")}
            >
              최근 심방 <SortIcon columnKey="lastVisit" />
            </div>
            <div className="flex items-center cursor-default">비고</div>
          </div>

          <div className="flex flex-col gap-2 pb-24">
            {sortedStudents.map((s, idx) => {
              const isSelected = selectedIds.includes(s.id);
              return (
                <div
                  key={s.id}
                  id={idx === 0 ? "tour-student-card" : undefined}
                  onClick={() => handleSelectRow(s.id)}
                  className={`grid grid-cols-[50px_1.5fr_1fr_1.5fr_0.8fr_0.8fr_0.8fr_1fr] gap-2 items-center p-3 rounded-[1.8rem] border transition-all duration-300 cursor-pointer group relative overflow-hidden ${isSelected
                    ? "bg-[#354F38]/5 border-[#354F38] ring-2 ring-[#354F38]/10 z-10 scale-[1.01]"
                    : "bg-white border-gray-100 hover:border-gray-300 hover:shadow-lg hover:-translate-y-0.5"
                    }`}
                >
                  <div className="flex justify-center">
                    <div
                      className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${isSelected
                        ? "bg-[#354F38] border-[#354F38] scale-110"
                        : "border-gray-200 group-hover:border-gray-300 bg-white"
                        }`}
                    >
                      {isSelected && <Check size={12} className="text-white" />}
                    </div>
                  </div>

                  <div
                    className="flex items-center gap-3 min-w-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewingStudent(s);
                    }}
                  >
                    <div className="relative group/avatar flex-shrink-0">
                      <div className="w-10 h-10 rounded-[14px] bg-gray-50 border border-white shadow-sm overflow-hidden">
                        {s.photoUrl ? (
                          <img src={s.photoUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <img
                            src={s.gender === "남" ? "/images/boy_3d_light.png" : "/images/girl_3d_light.png"}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      {s.faithState === "초신자" && (
                        <div className="absolute -bottom-1 -right-1 bg-yellow-400 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full border border-white shadow-sm z-20">
                          NEW
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-gray-900 text-sm truncate">{s.name}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${userProfile?.departmentColors?.[s.grade]
                            ? `text-white ${userProfile.departmentColors[s.grade]}`
                            : "bg-gray-100 text-gray-500"
                            }`}
                        >
                          {s.grade}
                        </span>
                        {s.class && (
                          <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded-md">
                            {s.class}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Cake size={12} className="text-gray-400 flex-shrink-0" />
                      <span className="text-xs font-bold text-gray-600 truncate">{s.birth || "-"}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <School size={12} className="text-gray-400 flex-shrink-0" />
                      <span className="text-[10px] text-gray-400 font-bold truncate">{s.school || "-"}</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-1.5">
                      <Phone size={12} className="text-gray-400 flex-shrink-0" />
                      <span className="text-xs font-bold text-gray-600 font-mono tracking-wide truncate">
                        {s.phone || "-"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 overflow-hidden">
                      <MapPin size={12} className="text-gray-400 flex-shrink-0" />
                      <span className="text-[10px] text-gray-400 truncate" title={s.address || "-"}>
                        {s.address || "-"}
                      </span>
                    </div>
                  </div>

                  <div>
                    {s.faithState ? (
                      <span
                        className={`inline-block w-[60px] py-1 rounded-lg text-[10px] font-bold border text-center truncate ${faithStateStyles[s.faithState]
                          }`}
                      >
                        {s.faithState}
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </div>

                  <div className="flex items-center">
                    <span
                      className={`text-[10px] font-bold px-2 py-1 rounded-lg ${s.lastAttendance ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-400"
                        }`}
                    >
                      {getRelativeTime(s.lastAttendance)}
                    </span>
                  </div>

                  <div className="flex items-center">
                    <span
                      className={`text-[10px] font-bold px-2 py-1 rounded-lg ${s.lastVisit ? "bg-orange-50 text-orange-600" : "bg-gray-50 text-gray-400"
                        }`}
                    >
                      {getRelativeTime(s.lastVisit)}
                    </span>
                  </div>

                  <div className="min-w-0 overflow-hidden">
                    <span className="text-[10px] text-gray-400 truncate block" title={s.notes}>
                      {s.notes || "-"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {sortedStudents.length === 0 && (
            <div className="py-6 relative">
              <div className="flex flex-col items-center z-10">
                <span className="bg-[#354F38] text-white border-2 border-white text-sm font-black px-5 py-2.5 rounded-full shadow-2xl inline-block whitespace-nowrap mb-3">
                  ✨ 첫 학생을 등록해보세요!
                </span>
                <button
                  onClick={openAddModal}
                  className="bg-white text-[#354F38] border border-gray-100 font-bold px-6 py-2 rounded-full shadow-xl hover:scale-105 transition-transform"
                >
                  지금 등록하기
                </button>
              </div>
              <div className="grid grid-cols-[50px_1.5fr_1fr_1.5fr_0.8fr_0.8fr_0.8fr_1fr] gap-2 items-center p-3 rounded-[1.8rem] border border-dashed border-gray-300 opacity-40 bg-gray-50 select-none">
                <div className="flex justify-center">
                  <div className="w-5 h-5 rounded-lg border-2 border-gray-200 bg-white"></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-[14px] bg-gray-200"></div>
                  <div className="flex-1 space-y-1">
                    <div className="w-16 h-4 bg-gray-200 rounded"></div>
                    <div className="w-10 h-3 bg-gray-200 rounded"></div>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="w-20 h-3 bg-gray-200 rounded"></div>
                  <div className="w-16 h-3 bg-gray-200 rounded"></div>
                </div>
                <div className="space-y-1">
                  <div className="w-24 h-3 bg-gray-200 rounded"></div>
                  <div className="w-16 h-3 bg-gray-200 rounded"></div>
                </div>
                <div>
                  <div className="w-12 h-6 bg-gray-200 rounded-lg"></div>
                </div>
                <div>
                  <div className="w-12 h-6 bg-gray-200 rounded-lg"></div>
                </div>
                <div>
                  <div className="w-12 h-6 bg-gray-200 rounded-lg"></div>
                </div>
                <div>
                  <div className="w-full h-4 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
